import ErrorMessage from "components/ErrorMessage";
import OButton from "components/reusable/OButton";
import useToastContext from "hooks/useToastContext";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { apiGet, apiPost } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import { useTranslation } from "react-i18next";
import OInputField from "components/reusable/OInputField";
import formValidation from "utils/formValidation";
import OTextArea from "components/reusable/OTextArea";
import OSelect from "components/reusable/OSelect";
import {
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogContent,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { FiFilter, FiMail, FiUsers } from "react-icons/fi";
import { LuLoader } from "react-icons/lu";

function getUserLabel(user) {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  if (name) return `${user?.firstName} ${user?.lastName}`;
  return name || user?.email || "";
}

function SectionHeader({ icon: Icon, title }) {
  return (
    <div className="mb-5 flex items-center gap-2 border-b border-gray-100 pb-4">
      <Icon className="h-4 w-4 text-gray-500" />
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
    </div>
  );
}

function PanelCard({ children, className = "" }) {
  return (
    <div
      className={`flex flex-col overflow-hidden rounded-xl border border-[#E9EDF9] bg-white shadow-sm ${className}`}
    >
      <div className="flex-1 overflow-y-auto p-5">{children}</div>
    </div>
  );
}

const NotificationAdd = ({ open, onClose, getAllNotifications }) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    mode: "onBlur",
    shouldFocusError: true,
  });
  const notification = useToastContext();
  const [availableFor, setAvailableFor] = useState("");
  const [userError, setUserError] = useState("");
  const [userIds, setUserIds] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isAllUser, setIsAllUser] = useState(false);
  const [allUsersCount, setAllUsersCount] = useState(null);
  const [sending, setSending] = useState(false);
  const userType = watch("userType");

  const recipientCount = useMemo(() => {
    if (isAllUser) return allUsersCount;
    return selectedUsers.length;
  }, [isAllUser, allUsersCount, selectedUsers.length]);

  useEffect(() => {
    if (!open) {
      reset();
      setAvailableFor("");
      setUserError("");
      setUserIds([]);
      setSelectedUsers([]);
      setSearchInput("");
      setSearchOptions([]);
      setIsAllUser(false);
      setAllUsersCount(null);
    }
  }, [open, reset]);

  useEffect(() => {
    setSelectedUsers([]);
    setUserIds([]);
    setSearchInput("");
    setSearchOptions([]);
    setAllUsersCount(null);
    if (!isAllUser) {
      setAvailableFor("");
    }
  }, [userType]);

  useEffect(() => {
    if (!isAllUser || !userType) {
      setAllUsersCount(null);
      return;
    }

    const fetchAllUsersCount = async () => {
      try {
        const path = apiPath.notificationRecipientsCount;
        const payload = { userType: userType };
        const result = await apiGet(path, payload);
        if (result?.data?.success) {
          setAllUsersCount(result?.data?.results);
        }
      } catch (error) {
        console.log("error fetching recipient count==>>>>", error.message);
      }
    };

    fetchAllUsersCount();
  }, [isAllUser, userType]);

  useEffect(() => {
    if (!open || isAllUser || !userType) {
      setSearchOptions([]);
      return;
    }

    const keyword = searchInput.trim();
    if (!keyword) {
      setSearchOptions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const path = userType === "user" ? apiPath.getUsers : apiPath.getAgents;
        const payload =
          userType === "user"
            ? { page: 1, page_size: 20, keyword, status: "active" }
            : { page: 1, pageSize: 20, keyword, status: "active" };
        const result = await apiGet(path, payload);
        if (result?.data?.success) {
          setSearchOptions(result?.data?.results?.docs ?? []);
        } else {
          setSearchOptions([]);
        }
      } catch (error) {
        console.log("error in user search==>>>>", error.message);
        setSearchOptions([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchInput, userType, isAllUser, open]);

  const handleSelectAllUsers = (event) => {
    if (event.target.checked) {
      setIsAllUser(true);
      setSelectedUsers([]);
      setUserIds([]);
      setSearchInput("");
      setSearchOptions([]);
      setAvailableFor("all");
      setUserError("");
    } else {
      setIsAllUser(false);
      setAllUsersCount(null);
      if (selectedUsers.length === 0) {
        setUserError("Please select at least one user.");
        setAvailableFor("");
      }
    }
  };

  const handleUsersChange = (_, newValue) => {
    setSelectedUsers(newValue);
    setUserIds(newValue.map((user) => user._id));
    if (newValue.length > 0) {
      setAvailableFor("specific");
      setUserError("");
    } else if (!isAllUser) {
      setAvailableFor("");
      setUserError("Please select at least one user.");
    }
  };

  const onSubmit = async (data) => {
    try {
      setSending(true);
      if (!availableFor) {
        setUserError("Please select at least one user.");
        return;
      }
      const obj = { ...data, sendTo: availableFor };
      if (userIds.length > 0) {
        obj.user = userIds;
      }
      const res = await apiPost(apiPath.notifications, {
        ...obj,
      });
      if (res.data.success) {
        setSending(false);
        setValue("title", "");
        setValue("titleAr", "");
        setValue("description", "");
        setValue("descriptionAr", "");
        onClose();
        notification.success(res?.data?.message);
        getAllNotifications();
      } else {
        notification.error(res?.data?.message);
      }
    } catch (err) {
      console.error("err:", err);
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { bgcolor: "#f3f4f6" },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div className="flex-col h-full w-full max-w-[1600px] mx-auto px-4 py-5 sm:px-6 lg:px-8">
          <header className="rounded-xl border border-[#E9EDF9] bg-white shadow-sm p-3 mb-5 flex shrink-0 items-center justify-between gap-4">
            <h2 className="truncate text-xl font-semibold text-gray-900">
              {t("ADD_NOTIFICATION")}
            </h2>

            <div className="flex shrink-0 items-center gap-3">
              <OButton
                style={{ color: "black" }}
                extraClasses={"bg-[#E1E1E1] hover:bg-[#d4d4d4]"}
                label={<>{t("O_BACK")}</>}
                onClick={onClose}
              />
              {sending ? (
                <LuLoader className="animate-spin" />
              ) : (
                <OButton
                  label={<>{t("O_ADD")}</>}
                  type="submit"
                  onClick={handleSubmit(onSubmit)}
                />
              )}
            </div>
          </header>

          <div className="mx-auto w-full h-full grid flex-1 grid-cols-1 gap-4 lg:grid-cols-[380px_minmax(0,1fr)_380px]">
            <PanelCard className="min-h-[320px] lg:min-h-0">
              <SectionHeader icon={FiFilter} title={t("AUDIENCE_FILTERS")} />

              <div className="space-y-4">
                <OSelect
                  style={{ "background-color": "white" }}
                  wrapperClassName=""
                  name="userType"
                  inputLabel={t("O_USER_TYPE")}
                  removeDefault
                  options={[
                    { key: "User", keyAr: "مستخدم", value: "user" },
                    { key: "Agent", keyAr: "وكيل", value: "agent" },
                  ]}
                  register={register("userType")}
                />

                <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-3">
                  <div className="flex items-center">
                    <input
                      id="notification-all-users"
                      type="checkbox"
                      checked={isAllUser}
                      onChange={handleSelectAllUsers}
                      className="h-4 w-4 cursor-pointer rounded border-gray-300 text-[#8FC150] focus:ring-[#8FC150]"
                    />
                    <label
                      htmlFor="notification-all-users"
                      className="ml-2 cursor-pointer text-sm font-medium text-gray-900"
                    >
                      {t("O_ALL_USERS")}
                    </label>
                  </div>
                </div>

                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  disabled={isAllUser || !userType}
                  options={searchOptions}
                  loading={searchLoading}
                  filterOptions={(options) => options}
                  getOptionLabel={getUserLabel}
                  isOptionEqualToValue={(option, value) =>
                    option._id === value._id
                  }
                  value={selectedUsers}
                  onChange={handleUsersChange}
                  onInputChange={(_, value, reason) => {
                    if (reason === "input") {
                      setSearchInput(value);
                    }
                  }}
                  noOptionsText={
                    searchInput.trim()
                      ? t("O_NO_RECORDS_FOUND") || "No users found"
                      : "Type to search users"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("MERCHANT_SEARCH_BY_EMAIL")}
                      placeholder="Search by name or email..."
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#fff",
                          borderRadius: "10px",
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {searchLoading ? (
                              <CircularProgress color="inherit" size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                {userError && <ErrorMessage message={userError} />}
              </div>
            </PanelCard>

            <PanelCard className="min-h-[420px] lg:min-h-0">
              <SectionHeader icon={FiMail} title="Compose" />

              <div className="grid gap-5">
                <OInputField
                  wrapperClassName=""
                  name="title"
                  inputLabel={
                    <>
                      {t("O_TITLE")}
                      <span className="text-red-500">*</span>
                    </>
                  }
                  type="text"
                  autoFocus
                  register={register("title", formValidation.title)}
                  errors={errors}
                />

                <OInputField
                  wrapperClassName=""
                  name="titleAr"
                  inputLabel={
                    <>
                      {t("O_TITLE") + t("O_ARABIC")}
                      <span className="text-red-500">*</span>
                    </>
                  }
                  type="text"
                  register={register("titleAr", formValidation.titleAr)}
                  errors={errors}
                />

                <OTextArea
                  name="description"
                  style={{"background-color":"white"}}
                  inputLabel={
                    <>
                      {t("O_MESSAGE")}
                      <span className="text-red-500">*</span>
                    </>
                  }
                  type="textarea"
                  register={register("description", formValidation.description)}
                  errors={errors}
                />

                <OTextArea
                  wrapperClassName="relative z-0 w-full group"
                  style={{"background-color":"white"}}
                  name="descriptionAr"
                  inputLabel={
                    <>
                      {t("O_MESSAGE") + t("O_ARABIC")}
                      <span className="text-red-500">*</span>
                    </>
                  }
                  type="textarea"
                  register={register(
                    "descriptionAr",
                    formValidation.descriptionAr,
                  )}
                  errors={errors}
                />
              </div>
            </PanelCard>

            <PanelCard className="min-h-[200px] lg:min-h-0">
              <SectionHeader icon={FiUsers} title="Recipients" />

              <div className="rounded-xl border border-gray-100 bg-gray-50 p-6 text-center">
                <p className="text-4xl font-semibold text-gray-900">
                  {recipientCount ?? "—"}
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  {isAllUser ? t("O_ALL_USERS") : "Selected recipients"}
                </p>
              </div>
            </PanelCard>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationAdd;
